import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import {
  StartInspectionInstanceMutation,
  StartInspectionInstanceMutationVariables,
} from 'graphql-query-contracts';
import { startInspectionInstanceMutation } from 'graphql-queries';
import { StartInspectionError } from './errors/StartInspectionError';

export type StartInspectionInput = {
  instanceId: string;
  channelId: string;
  startedAt: string;
};

type InspectionEventResult = StartInspectionInstanceMutation['startInspectionInstance'];

export type UseStartInspectionResult = {
  startInspection: (input: StartInspectionInput) => Promise<InspectionEventResult>;
  loading: boolean;
  error: Error | undefined;
};

export const useStartInspection = (): UseStartInspectionResult => {
  const [startInstance, { loading, error }] = useMutation<
    StartInspectionInstanceMutation,
    StartInspectionInstanceMutationVariables
  >(startInspectionInstanceMutation);

  const startInspection = useCallback(
    async (input: StartInspectionInput): Promise<InspectionEventResult> => {
      try {
        const startVariables: StartInspectionInstanceMutationVariables = {
          input: {
            instanceId: input.instanceId,
            channelId: input.channelId,
            startedAt: input.startedAt,
          },
        };

        const startResult = await startInstance({ variables: startVariables });

        if (!startResult.data?.startInspectionInstance) {
          throw new StartInspectionError('Failed to start inspection instance');
        }

        return startResult.data.startInspectionInstance;
      } catch (err) {
        const wrappedError = err instanceof StartInspectionError
          ? err
          : new StartInspectionError(err instanceof Error ? err.message : 'Failed to start inspection instance');
        throw wrappedError;
      }
    },
    [startInstance]
  );

  return {
    startInspection,
    loading,
    error,
  };
};
